import { Alert, Button, Card, Container, IconButton, LinearProgress, MenuItem, Stack, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import axios, { endpoints, formatEndpoint } from "src/utils/axios";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { DisplayPageData } from "src/components/display-page-data";
import NoneFoundReport from "src/components/none-found-report";
import PageFilterLoader, { usePageFilterLoaderData } from "src/components/page-filter-loader";
import { useSettingsContext } from "src/components/settings";
import PageHeader from "src/components/page-header";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import { EldDriver, EldInspection } from "src/models";
import { addDaysToDate, formatDateForPreview, formatTimeForPreview, today } from "src/utils/date-utils";
import { useSort } from "src/hooks/use-sort";
import Iconify from "src/components/iconify";
import { enqueueSnackbar } from "notistack";
import useModelSavingData from "src/hooks/use-model-saving-data";
import dayjs from "dayjs";
import { useAuthContext } from "src/auth/hooks";
import SimpleDialog from "src/components/simple-dialog";
import Label from "src/components/label";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import TruxSimpleDateField from "src/components/inputs/trux-simple-date-field";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import { DateTime } from "luxon";
import { isIOS } from "../eld-driver-logs/single";
import { EldInspectionModal } from "./_eld_inspection_modal";

type EldInspectionListProps = {
    eldInspections: EldInspection[],
    onEldInspectionSelected: (a: EldInspection) => void
    onDeleteInspectionSelected: (a: EldInspection) => void
}

const EldInspections = memo(({ eldInspections, onEldInspectionSelected, onDeleteInspectionSelected }: EldInspectionListProps) => {
    const {sortedList, sortData} = useSort(eldInspections);

    if (!sortedList) {
        return null;
    }

    return ( <div>
        <Card>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TruxTableCell/>
                            <SortableHeader sortKey="eldDriverFirstName" align="left">
                                Driver
                            </SortableHeader>
                            <SortableHeader sortKey="inspectionTime" align="center">
                                Time
                            </SortableHeader>
                            <TruxTableCell align="left">
                                Inspection Type
                            </TruxTableCell>
                            <TruxTableCell align="left">
                                Unit Number
                            </TruxTableCell>
                            <TruxTableCell align="left">
                                User Comment
                            </TruxTableCell>
                            <TruxTableCell align="left">
                                FMCSA Comment
                            </TruxTableCell>
                            <TruxTableCell align="center">
                                ELD File Link
                            </TruxTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedList.map((eldInspection, index) => (
                            <TableRow
                                key={index}
                                hover
                                onClick={() => onEldInspectionSelected(eldInspection)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TruxTableCell align="center">
                                    {DateTime.fromISO(eldInspection.toEventDate) > DateTime.now().plus({ days: -55 })
                                    && eldInspection.inspectionType === "Inspected on Device" &&
                                        <IconButton onClick={(e) => {
                                            onDeleteInspectionSelected(eldInspection);
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}>
                                            <Iconify icon="solar:trash-bin-trash-bold"/>
                                        </IconButton>
                                    }
                                </TruxTableCell>
                                <TruxTableCell align="left">
                                    {`${eldInspection.eldDriverFirstName  } ${  eldInspection.eldDriverLastName}`}
                                </TruxTableCell>
                                <TruxTableCell align="center">
                                    {`${formatDateForPreview(eldInspection.inspectionTime)  } ${  formatTimeForPreview(eldInspection.inspectionTime)}`}
                                </TruxTableCell>
                                <TruxTableCell align="left">
                                    {eldInspection.inspectionType}
                                </TruxTableCell>
                                <TruxTableCell align="left">
                                    {eldInspection.vehicleUnitNumber}
                                </TruxTableCell>
                                <TruxTableCell align="left">
                                    {eldInspection.userComment || ""}
                                </TruxTableCell>
                                <TruxTableCell align="left">
                                    {eldInspection.fmcsaComment || ""}
                                </TruxTableCell>
                                <TruxTableCell align="center">
                                    {eldInspection.logFileUrl &&
                                        <IconButton href={eldInspection.logFileUrl} target="_blank" onClick={(e) => e.stopPropagation()}>
                                            <Iconify icon="mage:file-download"/>
                                        </IconButton>
                                    }
                                </TruxTableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    </div>
    );
});

type PageModel = {
    eldInspections?: EldInspection[];
    eldDrivers?: EldDriver[];
}

export default function Page() {
    const settings = useSettingsContext();
    const pageFilterData = usePageFilterLoaderData(
        addDaysToDate(today(), -5),
        today()
    );
    const [isOpen, setIsOpen] = useState(false);

    const selectedEldInspectionModel = useModelSavingData<EldInspection>(endpoints.eldInspections.update);
    const [deleteInspectionOpen, setDeleteInspectionOpen] = useState<boolean>(false);

    const dataLoader = useCallback(() => {
        if (!pageFilterData?.fromDate || !pageFilterData?.toDate) {
            console.error("Missing fromDate or toDate");
            return { eldInspections: [] };
        }
        return {
            eldInspections: axios.get(
                formatEndpoint(endpoints.eldInspections.findAll, {
                    fromDate: pageFilterData.fromDate,
                    toDate: pageFilterData.toDate,
                })
            ).then(response => response.data || []),
            eldDrivers: axios.get(endpoints.eldDrivers.findAll).then(response => response.data),
        };
    }, [pageFilterData]);
    const {
        data,
        updateData,
        setData,
        error,
        loaded,
        loading,
        setShouldRefresh,
    } = useModelData<PageModel>(dataLoader);

    useEffect(() => {
        if (!loading && error) {
          alert(error);
        }
    }, [loading, error]);

    const onEldInspectionSelected = useCallback((a: EldInspection) => {
        selectedEldInspectionModel.set({...a});
        setIsOpen(true);
    }, [selectedEldInspectionModel, setIsOpen]);

    const onDeleteInspectionSelected = useCallback((a: EldInspection) => {
        selectedEldInspectionModel.set({...a});
        setDeleteInspectionOpen(true);
    }, [selectedEldInspectionModel, setDeleteInspectionOpen]);

    const updateEldInspection = () => {
        selectedEldInspectionModel.save().then(response => {
            if (response) {
            enqueueSnackbar("Successfully saved the Eld Inspection");
            const eldInspection = response.data as EldInspection;

            updateData("eldInspections")(replaceById(data!.eldInspections!, eldInspection));
            setIsOpen(false);
            }
        });
    }

    const { session } = useAuthContext();

    const [sendToFmcsaOpen, setSendToFmcsaOpen] = useState(false);
    const timezone = session?.organization?.companyTimezone as string;

    const [fromDate, setFromDate] = useState(dayjs().add(-8, 'days').tz(timezone || 'America/Chicago').format("YYYY-MM-DD"));
    const [toDate, setToDate] = useState(dayjs().tz(timezone || 'America/Chicago').format("YYYY-MM-DD"));
    const [eldDriverId, setEldDriverId] = useState(0);
    const [viaEmail, setViaEmail] = useState(false);
    const [fmcsaComment, setFmcsaComment] = useState("");
    const [dialogLoading, setDialogLoading] = useState(false);

    const [downloadLogsOpen, setDownloadLogsOpen] = useState(false);
    const [logsFromDate, setLogsFromDate] = useState(DateTime.now().setZone(timezone || "America/Chicago").minus({days: 8}).toFormat("yyyy-MM-dd"));
    const [logsToDate, setLogsToDate] = useState(DateTime.now().setZone(timezone || "America/Chicago").toFormat("yyyy-MM-dd"));

    const [fileFormat, setFileFormat] = useState<"pdf" | "fmcsa">("pdf");

    const selectedEldDriver = data?.eldDrivers?.find(eldDriver => eldDriver.id === eldDriverId);

    const abortControllerRef = useRef<AbortController | null>(null);

    const downloadLogsButton = <Button
        onClick={() => {
        setFromDate(dayjs().add(-8, 'days').tz(timezone || 'America/Chicago').format("YYYY-MM-DD"));
        setToDate(dayjs().tz(timezone || 'America/Chicago').format("YYYY-MM-DD"));
          setDownloadLogsOpen(true);
        }}
        variant="outlined"
        color="error"
        sx={{
            boxSizing: 'border-box',
            borderWidth: '1.5px',
            borderStyle: 'solid',
            borderColor: '#ff5631',
            fontWeight: 900,
            '&:hover': {
                borderWidth: '1.5px',
                borderColor: '#ff5631',
                backgroundColor: 'rgba(255,86,49,0.08)',
            },
        }}
        startIcon={<Iconify icon="mage:file-download" />}
    >
        Download Logs
    </Button>;

    const sendInspectionButton = <Button
        onClick={() => {
        setFromDate(dayjs().add(-8, 'days').tz(timezone || 'America/Chicago').format("YYYY-MM-DD"));
        setToDate(dayjs().tz(timezone || 'America/Chicago').format("YYYY-MM-DD"));
          setSendToFmcsaOpen(true);
        }}
        variant="contained"
        color="error"
        startIcon={<Iconify icon="si:error-fill" />}
      >
        Send Logs to FMCSA
      </Button>;

    return (
        <Container maxWidth={settings.themeStretch ? false : 'lg'}>
            <DisplayPageData loading={loading} error={error} data={data}>
                <PageHeader title="Inspections">
                    {downloadLogsButton}
                    {sendInspectionButton}
                </PageHeader>
                <PageFilterLoader p={0} pageFilterData={pageFilterData} onRun={() => {
                    setShouldRefresh(true);
                }} />

                {data && (data.eldInspections?.length || 0) > 0 && <EldInspections eldInspections={data.eldInspections || []} onEldInspectionSelected={onEldInspectionSelected} onDeleteInspectionSelected={onDeleteInspectionSelected}/>}

                {loaded && data && data.eldInspections?.length === 0 && <NoneFoundReport />}

                {data && isOpen && <EldInspectionModal
                    selectedEldInspectionModel={selectedEldInspectionModel}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    updateEldInspection={updateEldInspection}
                />}
            </DisplayPageData>


            {sendToFmcsaOpen &&
            <SimpleDialog
            transitionDuration={0}
            title={<>Send to FMCSA <Label variant="filled" color="error">FMCSA EXPORT</Label></>}
            open={sendToFmcsaOpen}
            onCancel={() => setSendToFmcsaOpen(false)}
            onSubmit={() => {
                if (eldDriverId === 0) {
                    enqueueSnackbar("Please select a driver", {variant: "error"});
                    return;
                }

                setDialogLoading(true);
                axios.post(
                    endpoints.eldInspections.sendLogsToFmcsa, {
                        eldDriverId,
                        fromDate,
                        toDate,
                        viaEmail,
                        comment: fmcsaComment,
                    }
                ).then(() => {
                    enqueueSnackbar("Successfully transmitted your data to the FMCSA");
                    setShouldRefresh(true);
                    setSendToFmcsaOpen(false);
                }).catch((errorResponse) => {
                    if (errorResponse?.response?.status === 401) {
                        enqueueSnackbar("You don't have permission to perform Inspections. Please ask the Account owner to perform this action.", {variant: "error"});
                    } else {
                        enqueueSnackbar("Your data could not be transmitted.", {variant: "error"});
                    }
                }).finally(() => {
                    setDialogLoading(false);
                })
            }}
            submitButton="Send to FMCSA"
            submitButtonIcon={<Iconify icon="si:error-fill" />}
            submitButtonColor="error"
            submitDisabled={dialogLoading}
            >
            <Stack direction="column" gap={1}>
                <Alert icon={<Iconify icon="solar:danger-bold" />} severity='error'>
                    The following action is not reversible. Your logs will be sent to FMCSA.
                </Alert>

                <TruxFormField label="Driver">
                    <TruxAutocompleteField
                    options={data?.eldDrivers ? [...data.eldDrivers.map(eldDriver => ({
                        label: `${eldDriver.firstName} ${eldDriver.lastName}`,
                        id: eldDriver.id
                    })), {
                        label: "None",
                        id: 0
                    }] : []}
                    onClear={() => {
                        setEldDriverId(0);
                    }}
                    onChange={(value) => {
                        setEldDriverId(eldDriverIdCurrent => value === null ? eldDriverIdCurrent : Number(value?.id));
                    }}
                    selected={{
                        id: eldDriverId,
                        label: eldDriverId === 0 || !selectedEldDriver ? "None" : `${selectedEldDriver.firstName} ${selectedEldDriver.lastName}`,
                    }}
                    />
                </TruxFormField>

                <TruxFormField label="Type">
                    <TruxSimpleSelectField value={viaEmail ? "Email" : "Web Service"} onChange={val => setViaEmail(val === "Email")} >
                        <MenuItem value="Web Service">Web Service</MenuItem>
                        <MenuItem value="Email">Email</MenuItem>
                    </TruxSimpleSelectField>
                </TruxFormField>


                <TruxFormField label="FMCSA Comment">
                    <TruxSimpleTextField value={fmcsaComment} onChange={val => setFmcsaComment(val || "")} />
                </TruxFormField>

                <TruxFormField label="From Date" >
                    <TruxSimpleDateField limitWidth={false} value={fromDate} onChange={(val) => setFromDate(val!)} />
                </TruxFormField>
                <TruxFormField label="To Date">
                    <TruxSimpleDateField limitWidth={false} value={toDate} onChange={(val) => setToDate(val!)} />
                </TruxFormField>
            </Stack>
            </SimpleDialog>}


            {downloadLogsOpen &&
            <SimpleDialog
            transitionDuration={0}
            title="Download Logs"
            open={downloadLogsOpen}
            onCancel={() => {
                if (dialogLoading) {
                    return;
                }
                setDownloadLogsOpen(false);
                abortControllerRef.current?.abort();
            }}
            onSubmit={() => {
                if (eldDriverId === 0) {
                    enqueueSnackbar("Please select a driver.", {variant: "error"});
                    return;
                }
                console.log(`${eldDriverId  } DRIVER ID`);

                if (logsToDate < logsFromDate) {
                    enqueueSnackbar("The start date cannot be greater than the end date.", {variant: "error"});
                    return;
                }

                if (addDaysToDate(logsFromDate, 60) < logsToDate) {
                    enqueueSnackbar("Please select a range up to 60 days.", {variant: "error"});
                    return;
                }

                const controller = new AbortController();
                abortControllerRef.current = controller;
                setDialogLoading(true);
                axios({
                    url: endpoints.eldInspections.downloadLogs,
                    data: {eldDriverId, fromDate: logsFromDate, toDate: logsToDate, fileFormat},
                    method: 'POST',
                    responseType: 'blob',
                    signal: controller.signal}
                ).then(async (response) => {
                    if (fileFormat === "fmcsa") {
                        // FMCSA format - download only as .eld file
                        const blob = new Blob([response.data], { type: 'text/plain' });
                        const blobUrl = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        const filename = `${selectedEldDriver?.firstName || 'driver'}_${selectedEldDriver?.lastName || 'logs'}_${logsFromDate}_${logsToDate}.eld`;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(blobUrl);
                        setDownloadLogsOpen(false);
                    } else {
                        // PDF - opens in new tab
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const getBase64 = () => new Promise<string>((resolve) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result as string);
                                reader.readAsDataURL(blob);
                        });
                        const base64String = await getBase64();
                        const base64ToArrayBuffer = (_base64Str: string) => {
                            const binaryString = window.atob(_base64Str);
                            const binaryLen = binaryString.length;
                            const bytes = new Uint8Array(binaryLen);
                            for (let i = 0; i < binaryLen; i+=1) {
                                  const ascii = binaryString.charCodeAt(i);
                                  bytes[i] = ascii;
                           }
                            return bytes;
                        };

                        const showDocument = (_base64Str: string, _contentType: string) => {
                            const byte = base64ToArrayBuffer(_base64Str);
                            const blobInner = new Blob([byte], { type: _contentType });
                            const blobUrl = URL.createObjectURL(blobInner);
                            if (isIOS()) {
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.click();
                            } else {
                                const proxy = window.open(blobUrl, "_blank");
                                if (proxy === null) {
                                    enqueueSnackbar("Please allow popups for the pdf to load.", { variant: "error" });
                                }
                            }
                        };
                        showDocument(base64String.split("data:application/pdf;base64,").join(""), "application/pdf");
                        setDownloadLogsOpen(false);
                    }
                }).catch((err) => {
                    if (err.name !== 'CanceledError') {
                        enqueueSnackbar("Your file could not be downloaded.", {variant: "error"});
                    }
                }).finally(() => {
                    setDialogLoading(false);
                    abortControllerRef.current = null;
                })
            }}
            submitButton={fileFormat === "fmcsa" ? "Download CSV" : "Download PDF"}
            submitButtonIcon={dialogLoading ? <LinearProgress
                      color="inherit"
                      sx={{
                        height: 2,
                        width: 1,
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                      }}
                    /> : <Iconify icon="mage:file-download" />}
            submitButtonColor="error"
            submitDisabled={dialogLoading}
            >
            <Stack direction="column" gap={1}>
                <TruxFormField label="Driver">
                    <TruxAutocompleteField
                    options={data?.eldDrivers ? [...data.eldDrivers.map(eldDriver => ({
                        label: `${eldDriver.firstName} ${eldDriver.lastName}`,
                        id: eldDriver.id
                    })), {
                        label: "None",
                        id: 0
                    }] : []}
                    onClear={() => {
                        setEldDriverId(0);
                    }}
                    onChange={(value) => {
                        setEldDriverId(eldDriverIdCurrent => value === null ? eldDriverIdCurrent : Number(value?.id));
                    }}
                    selected={{
                        id: eldDriverId,
                        label: eldDriverId === 0 || !selectedEldDriver ? "None" : `${selectedEldDriver.firstName} ${selectedEldDriver.lastName}`,
                    }}
                    />
                </TruxFormField>
                <TruxFormField label="From Date" >
                    <TruxSimpleDateField limitWidth={false} value={logsFromDate} onChange={(val) => setLogsFromDate(val!)} />
                </TruxFormField>
                <TruxFormField label="To Date">
                    <TruxSimpleDateField limitWidth={false} value={logsToDate} onChange={(val) => setLogsToDate(val!)} />
                </TruxFormField>
                <TruxFormField label="File Format">
                    <TruxSimpleSelectField value={fileFormat} onChange={val => setFileFormat(val as "pdf" | "fmcsa")} >
                        <MenuItem value="pdf">PDF (For Viewing)</MenuItem>
                        <MenuItem value="fmcsa">CSV (FMCSA Format)</MenuItem>
                    </TruxSimpleSelectField>
                </TruxFormField>
            </Stack>
            </SimpleDialog>}

            {deleteInspectionOpen &&
            <SimpleDialog
                title="Delete Inspection"
                open={deleteInspectionOpen}
                onCancel={() => setDeleteInspectionOpen(false)}
                onSubmit={() => {
                    if (!selectedEldInspectionModel.model) {
                        return;
                    }
                    axios.put(endpoints.eldInspections.deleteInspection, {
                        inspectionId: selectedEldInspectionModel.model.id,
                        eldDriverId: selectedEldInspectionModel.model.eldDriverId
                    }).then((response) => {
                        enqueueSnackbar("Inspection deleted successfully");
                        selectedEldInspectionModel.destroy().then(() => {
                            updateData("eldInspections")(deleteById(data!.eldInspections!, selectedEldInspectionModel.model!));
                        })
                        setDeleteInspectionOpen(false);
                    }).catch(() => {
                        enqueueSnackbar("Unexpected error has occurred.", {variant: "error"});
                    })
                }}
                submitDisabled={dialogLoading}
                submitButton="Delete Inspection"
                submitButtonIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                submitButtonColor="error"
            >
                Are you sure you would like to delete this inspection?
            </SimpleDialog>}
        </Container>
    );
}
